import React from 'react';
import useAnimeReveal from '../hooks/useAnimeReveal';
import useAnimePageLoad from '../hooks/useAnimePageLoad';
import leaseVehicleHero from '../assets/AppImages/lease-vehicle-hero.jpg';
import leaseVehicleBenefits from '../assets/AppImages/lease-vehicle-benefits.jpg';

const whatsappMessage = encodeURIComponent(
  'Hello DeRoyalty Car Rental, I would like to lease my vehicle to you for rental. Please tell me what information you need to assess my vehicle.'
);
const emailSubject = encodeURIComponent('Lease My Vehicle Enquiry');
const emailBody = encodeURIComponent(
  'Hello DeRoyalty Car Rental,\n\nI would like to lease my vehicle to you for rental. Please contact me with the next steps and the information required for assessment.\n\nThank you.'
);

export const leaseWhatsAppHref = `https://wa.me/263712768037?text=${whatsappMessage}`;
export const leaseEmailHref = `mailto:customercare@deroyaltycarrental.com?subject=${emailSubject}&body=${emailBody}`;

const processSteps = [
  {
    number: '01',
    icon: 'bi-chat-square-text',
    title: 'Tell us about your vehicle',
    text: 'Send the make, model, year, condition, location and a few clear photos through WhatsApp or email.',
  },
  {
    number: '02',
    icon: 'bi-shield-check',
    title: 'Vehicle assessment',
    text: 'Our team reviews the vehicle, its documents and rental suitability before discussing an arrangement with you.',
  },
  {
    number: '03',
    icon: 'bi-car-front',
    title: 'Rental preparation',
    text: 'Once accepted and agreed, the vehicle is prepared and presented to suitable DeRoyalty rental clients.',
  },
  {
    number: '04',
    icon: 'bi-graph-up-arrow',
    title: 'Start earning',
    text: 'Your vehicle can generate income through completed rentals under the terms agreed with DeRoyalty.',
  },
];

const ownerBenefits = [
  {
    icon: 'bi-cash-coin',
    title: 'Income from your vehicle',
    text: 'Turn suitable periods when your car is not being used into an opportunity to earn.',
  },
  {
    icon: 'bi-megaphone',
    title: 'Professional rental exposure',
    text: 'Benefit from DeRoyalty marketing, customer enquiries and an established rental presence.',
  },
  {
    icon: 'bi-calendar2-check',
    title: 'Coordinated bookings',
    text: 'Our team coordinates approved rental enquiries and availability with you.',
  },
  {
    icon: 'bi-clipboard2-check',
    title: 'Clear agreement first',
    text: 'Commercial terms, responsibilities and vehicle availability are discussed before any rental activity begins.',
  },
];

const checklistItems = [
  'Vehicle make, model, year and current location',
  'Clear exterior and interior photographs',
  'Proof of ownership or authority to lease the vehicle',
  'Valid registration, insurance and roadworthiness documents',
  'Current condition, mileage and expected availability',
];

function LeaseYourVehicle() {
  const rootRef = useAnimePageLoad({ selector: '[data-anime-enter]' });
  useAnimeReveal({ selector: '[data-anime]', rootRef });

  return (
    <main className="lease-vehicle-page" ref={rootRef}>
      <section className="lease-vehicle-hero" aria-labelledby="lease-vehicle-title">
        <img
          src={leaseVehicleHero}
          alt="Modern vehicle available for a DeRoyalty rental partnership"
          width="2400"
          height="1600"
          fetchpriority="high"
          decoding="async"
          draggable="false"
        />
        <div className="lease-vehicle-hero-overlay" aria-hidden="true" />
        <div className="lease-vehicle-hero-copy" data-anime-enter>
          <div className="lease-vehicle-hero-panel">
            <p className="lease-vehicle-eyebrow">Earn with DeRoyalty</p>
            <h1 id="lease-vehicle-title">Lease Your Vehicle</h1>
            <p>
              Do you want to earn money with DeRoyalty Car Rental? Give us the opportunity to assess
              your car, and we can rent it out for you under an agreed vehicle partnership.
            </p>
          </div>
          <div className="lease-vehicle-hero-actions">
            <a
              href={leaseWhatsAppHref}
              target="_blank"
              rel="noopener noreferrer"
              className="lease-vehicle-action lease-vehicle-action-primary"
            >
              <i className="bi bi-whatsapp" aria-hidden="true" />
              Enquire on WhatsApp
            </a>
            <a href={leaseEmailHref} className="lease-vehicle-action lease-vehicle-action-secondary">
              <i className="bi bi-envelope" aria-hidden="true" />
              Send an Email
            </a>
          </div>
          <div className="lease-vehicle-hero-points" aria-label="Vehicle leasing highlights">
            <span><i className="bi bi-check2-circle" aria-hidden="true" /> Vehicle assessment</span>
            <span><i className="bi bi-check2-circle" aria-hidden="true" /> Managed enquiries</span>
            <span><i className="bi bi-check2-circle" aria-hidden="true" /> Agreed terms first</span>
          </div>
        </div>
      </section>

      <section className="lease-vehicle-intro lease-vehicle-section" data-anime aria-labelledby="lease-intro-title">
        <div>
          <p className="lease-vehicle-eyebrow">A smarter use for your car</p>
          <h2 id="lease-intro-title">Your vehicle could become an earning asset.</h2>
        </div>
        <div className="lease-vehicle-intro-copy">
          <p>
            If you own a suitable vehicle, DeRoyalty can consider it for our rental network. You keep
            ownership while an agreed arrangement allows the vehicle to serve approved rental demand.
          </p>
          <p>
            Every vehicle is reviewed first. Acceptance, earnings, availability, care responsibilities
            and all other commercial terms depend on the vehicle assessment and the final written agreement.
          </p>
        </div>
      </section>

      <section className="lease-vehicle-section lease-vehicle-process" data-anime aria-labelledby="lease-process-title">
        <div className="lease-vehicle-section-head">
          <p className="lease-vehicle-eyebrow">Simple, transparent process</p>
          <h2 id="lease-process-title">How leasing your vehicle works</h2>
          <p>Start with a conversation. We will guide you through each stage if your vehicle is suitable.</p>
        </div>
        <div className="lease-vehicle-process-grid" data-anime-stagger="true">
          {processSteps.map((step) => (
            <article key={step.number} className="lease-vehicle-process-card">
              <span>{step.number}</span>
              <i className={`bi ${step.icon}`} aria-hidden="true" />
              <h3>{step.title}</h3>
              <p>{step.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="lease-vehicle-section lease-vehicle-benefits" data-anime aria-labelledby="lease-benefits-title">
        <div className="lease-vehicle-benefits-visual">
          <img
            src={leaseVehicleBenefits}
            alt="Professionally presented luxury vehicle ready for rental demand"
          />
          <div>
            <span>Owner opportunity</span>
            <strong>Put your vehicle to work</strong>
          </div>
        </div>
        <div className="lease-vehicle-benefits-copy">
          <p className="lease-vehicle-eyebrow">Why partner with us?</p>
          <h2 id="lease-benefits-title">DeRoyalty helps connect your car with rental demand.</h2>
          <div className="lease-vehicle-benefit-list">
            {ownerBenefits.map((benefit) => (
              <article key={benefit.title}>
                <i className={`bi ${benefit.icon}`} aria-hidden="true" />
                <div>
                  <h3>{benefit.title}</h3>
                  <p>{benefit.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="lease-vehicle-section lease-vehicle-checklist" data-anime aria-labelledby="lease-checklist-title">
        <div className="lease-vehicle-checklist-copy">
          <p className="lease-vehicle-eyebrow">Prepare your enquiry</p>
          <h2 id="lease-checklist-title">What should you send us?</h2>
          <p>Providing these basics helps our team give you a faster and more useful first response.</p>
        </div>
        <ul>
          {checklistItems.map((item) => (
            <li key={item}>
              <i className="bi bi-check2" aria-hidden="true" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="lease-vehicle-contact" data-anime aria-labelledby="lease-contact-title">
        <div>
          <p className="lease-vehicle-eyebrow">Ready to start?</p>
          <h2 id="lease-contact-title">Let us assess your vehicle.</h2>
          <p>
            Speak directly with DeRoyalty Car Rental and tell us about the vehicle you would like us
            to consider for rental.
          </p>
        </div>
        <div className="lease-vehicle-contact-actions">
          <a
            href={leaseWhatsAppHref}
            target="_blank"
            rel="noopener noreferrer"
            className="lease-vehicle-contact-card"
          >
            <i className="bi bi-whatsapp" aria-hidden="true" />
            <span>
              <small>Chat with us</small>
              <strong>+263 712 768 037</strong>
            </span>
            <i className="bi bi-arrow-up-right" aria-hidden="true" />
          </a>
          <a href={leaseEmailHref} className="lease-vehicle-contact-card">
            <i className="bi bi-envelope" aria-hidden="true" />
            <span>
              <small>Email us</small>
              <strong>customercare@deroyaltycarrental.com</strong>
            </span>
            <i className="bi bi-arrow-up-right" aria-hidden="true" />
          </a>
        </div>
      </section>
    </main>
  );
}

export default LeaseYourVehicle;
